/**
 * Select Options for DataGrid Edit Cells
 *
 * Centralized options for all select/autocomplete fields used across grids.
 * These options are used by:
 * - SelectEditCell for dropdown fields
 * - IdArrayEditCell for reference fields (indirectly via entity configs)
 * - Filter components throughout the app
 *
 * Options follow the SelectOption interface from editCells/types.ts
 *
 * Migrated from: src/components/DataGrid/filterArrays.tsx
 */

import { SelectOption } from '../editCells/types'

// ============================================================================
// ORGANIZATION OPTIONS
// ============================================================================

export const ORG_STATUS_OPTIONS: SelectOption[] = [
  { value: 'Prospect', label: 'Prospect', color: 'rgb(93, 79, 251)' },
  { value: 'Onboarding', label: 'Onboarding', color: 'rgb(92, 179, 253)' },
  { value: 'Active', label: 'Active', color: 'rgb(152, 214, 129)' },
  { value: 'In-Progress', label: 'In-Progress', color: 'rgb(110, 156, 222)' },
  { value: 'Active (SEO)', label: 'Active (SEO)', color: 'rgb(152, 98, 161)' },
  { value: 'Active (Creative)', label: 'Active (Creative)', color: 'rgb(147, 111, 218)' },
  { value: 'Active (Website)', label: 'Active (Website)', color: 'rgb(102, 69, 169)' },
  { value: 'Active (Past Due)', label: 'Active (Past Due)', color: 'rgb(255, 131, 88)' },
  { value: 'Active (LTP)', label: 'Active (LTP)', color: 'rgb(51, 158, 205)' },
  { value: '30 day notice', label: '30 Day Notice', color: 'rgb(235, 77, 77)' },
  { value: 'Cancelled', label: 'Cancelled', color: 'rgb(0,0,0)' },
  { value: 'Inactive / Paused', label: 'Inactive / Paused', color: 'rgb(255, 145, 145)' },
  { value: 'Pending', label: 'Pending', color: 'rgb(187, 124, 239)' },
  { value: 'Parent', label: 'Parent', color: 'rgba(67, 85, 241, 1)' },
]

export const ORG_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Property Management Company', label: 'Property Management Company', color: 'rgb(93, 79, 251)' },
  { value: 'Ownership Group', label: 'Ownership Group', color: 'rgb(92, 179, 253)' },
  { value: 'Housing Property', label: 'Housing Property', color: 'rgb(152, 214, 129)' },
  { value: 'Parent Client', label: 'Parent Client', color: 'rgb(93, 79, 251)' },
  { value: 'Agency Whitelabel', label: 'Agency Whitelabel', color: 'rgb(92, 179, 253)' },
  { value: 'Organization', label: 'Organization', color: 'rgb(242, 140, 40)' },
]

export const VERTICAL_OPTIONS: SelectOption[] = [
  { value: 'Construction', label: 'Construction' },
  { value: 'Tourism', label: 'Tourism' },
  { value: 'Marketing Agency', label: 'Marketing Agency' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Entertainment', label: 'Entertainment' },
  { value: 'Mortgage', label: 'Mortgage' },
  { value: 'Real Estate', label: 'Real Estate' },
  { value: 'Restaurant (Food & Beverage)', label: 'Restaurant (Food & Beverage)' },
  { value: 'City/Government', label: 'City/Government' },
  { value: 'Financial Institution', label: 'Financial Institution' },
  { value: 'Technology', label: 'Technology' },
  { value: 'Medical', label: 'Medical' },
  { value: 'Lawyer', label: 'Lawyer' },
  { value: 'Education', label: 'Education' },
  { value: 'Student', label: 'Student' },
  { value: 'Multifamily', label: 'Multifamily' },
  { value: 'Senior', label: 'Senior' },
  { value: 'Homebuilding', label: 'Homebuilding' },
  { value: 'Build to rent', label: 'Build to Rent' },
  { value: 'Other', label: 'Other' },
]

export const TRACKING_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Standard', label: 'Standard', color: '#1976d2' },
  { value: 'Advanced', label: 'Advanced', color: '#9c27b0' },
]

export const DISCOVERY_OPTIONS: SelectOption[] = [
  { value: 'Referral', label: 'Referral' },
  { value: 'Google Search', label: 'Google Search' },
  { value: 'LinkedIn', label: 'LinkedIn' },
  { value: 'Trade Show', label: 'Trade Show' },
  { value: 'Cold Outreach', label: 'Cold Outreach' },
  { value: 'Website', label: 'Website' },
  { value: 'Social Media', label: 'Social Media' },
  { value: 'Other', label: 'Other' },
]

// ============================================================================
// DEAL OPTIONS
// ============================================================================

export const DEAL_PAGE_STATUS_OPTIONS: SelectOption[] = [
  { value: 'Prospect', label: 'Prospect', color: 'rgb(93, 79, 251)' },
  { value: 'Onboarding', label: 'Onboarding', color: 'rgb(92, 179, 253)' },
  { value: 'In-Progress', label: 'In-Progress', color: 'rgb(110, 156, 222)' },
  { value: 'Active', label: 'Active', color: 'rgb(152, 214, 129)' },
  { value: 'Active (SEO)', label: 'Active (SEO)', color: 'rgb(152, 98, 161)' },
  { value: 'Active (Creative)', label: 'Active (Creative)', color: 'rgb(147, 111, 218)' },
  { value: 'Active (Website)', label: 'Active (Website)', color: 'rgb(102, 69, 169)' },
  { value: 'Active (Past Due)', label: 'Active (Past Due)', color: 'rgb(255, 131, 88)' },
  { value: 'Active (LTP)', label: 'Active (LTP)', color: 'rgb(51, 158, 205)' },
  { value: '30 day notice', label: '30 Day Notice', color: 'rgb(235, 77, 77)' },
  { value: 'Cancelled', label: 'Cancelled', color: 'rgb(0,0,0)' },
  { value: 'Inactive / Paused', label: 'Inactive / Paused', color: 'rgb(255, 145, 145)' },
]

export const CONTRACT_STATUS_OPTIONS: SelectOption[] = [
  { value: 'Active', label: 'Active', color: 'rgb(75, 185, 114)' },
  { value: 'Approval Requested', label: 'Approval Requested', color: 'rgba(255, 148, 112, 1)' },
  { value: 'Completed', label: 'Completed', color: 'rgb(80, 200, 120)' },
  { value: 'Contract Approved', label: 'Contract Approved', color: 'rgb(255, 144, 69)' },
  { value: 'Created', label: 'Created', color: 'rgb(112, 224, 213)' },
  { value: 'Completed Internally', label: 'Completed Internally', color: 'rgba(22, 160, 133, 1)' },
  { value: 'Month to Month', label: 'Month to Month', color: 'rgb(23, 127, 217)' },
  { value: 'Needs Approval', label: 'Needs Approval', color: 'rgb(251, 193, 30)' },
  { value: 'Sent For Approval', label: 'Sent For Approval', color: 'rgb(163, 96, 205)' },
  { value: 'Saved', label: 'Saved', color: 'rgb(34, 121, 145)' },
  { value: 'Sent', label: 'Sent', color: 'rgb(76, 68, 198)' },
  { value: 'Set Status', label: 'Set Status', color: 'rgb(178, 190, 181)' },
  { value: 'Strategy Approved', label: 'Strategy Approved', color: 'rgb(6, 255, 167)' },
  { value: 'Viewed', label: 'Viewed', color: 'rgb(213, 102, 201)' },
]

export const DEAL_TYPE_OPTIONS: SelectOption[] = [
  { value: 'MSA', label: 'MSA', color: 'rgb(80, 200, 120)' },
  { value: 'MSA - Housing', label: 'MSA - Housing', color: 'rgb(80, 200, 120)' },
  { value: 'RMSA', label: 'RMSA', color: 'rgb(80, 200, 120)' },
  { value: 'RMSA - Housing', label: 'RMSA - Housing', color: 'rgb(80, 200, 120)' },
  { value: 'IO', label: 'Insertion Order', color: 'rgb(80, 200, 120)' },
  { value: 'CSA', label: 'CSA', color: 'rgb(150, 88, 238)' },
  { value: 'RCSA', label: 'RCSA', color: 'rgb(150, 88, 238)' },
]

export const DEAL_STATUS_OPTIONS: SelectOption[] = [
  { value: 'Month To Month', label: 'Month To Month', color: 'rgb(22, 138, 239)' },
  { value: 'Active', label: 'Active', color: 'rgb(80, 200, 120)' },
  { value: 'Active (system)', label: 'Active (system)', color: 'rgb(40, 249, 144)' },
  { value: 'In Progress', label: 'In Progress', color: 'rgb(253, 188, 100)' },
  { value: '30 Day Notice', label: '30 Day Notice', color: 'rgb(255, 123, 208)' },
  { value: 'Replaced', label: 'Replaced', color: 'rgb(239, 195, 124)' },
  { value: 'Pending', label: 'Pending', color: 'rgb(187, 124, 239)' },
  { value: 'Cancelled', label: 'Cancelled', color: 'rgb(0,0,0)' },
  { value: 'Ended (system)', label: 'Ended (system)', color: 'rgb(241, 9, 207)' },
  { value: 'Ended', label: 'Ended', color: 'rgba(220, 20, 60, 0.8)' },
]

export const DEAL_STAGE_OPTIONS: SelectOption[] = [
  { value: 'Discovery', label: 'Discovery', color: 'rgb(25, 117, 255)' },
  { value: 'Pending Approval', label: 'Pending Approval', color: 'rgba(241, 130, 141, 1)' },
  { value: 'Proposal', label: 'Proposal', color: 'rgba(178, 94, 178, 1)' },
  { value: 'Contract Created', label: 'Contract Created', color: 'rgb(11, 127, 171)' },
  { value: 'Contract Sent', label: 'Contract Sent', color: 'rgb(217, 169, 102)' },
  { value: 'Deal Won', label: 'Deal Won', color: 'rgba(80, 200, 120, 1)' },
  { value: 'Deal Lost', label: 'Deal Lost', color: '#ffb5ac' },
  { value: 'Cold', label: 'Cold', color: 'rgb(168, 202, 255)' },
  { value: 'Lost', label: 'Lost', color: '#ffdc85' },
  { value: 'Purgatory', label: 'Purgatory', color: '#ffdc85' },
]

// ============================================================================
// NCO (New Client Onboarding) OPTIONS
// ============================================================================

export const NCO_STATUS_OPTIONS: SelectOption[] = [
  { value: 'Staging', label: 'Staging', color: 'rgb(238, 111, 141)' },
  { value: 'Not Started', label: 'Not Started', color: 'rgb(153, 165, 165)' },
  { value: 'Bypass', label: 'Bypass', color: 'rgba(108, 122, 137, 1)' },
  { value: 'Pending', label: 'Pending', color: 'rgb(187, 124, 239)' },
  { value: 'On Hold', label: 'On Hold', color: 'rgb(238, 40, 99)' },
  { value: 'Onboarding', label: 'Onboarding', color: 'rgb(110, 193, 232)' },
  { value: 'Awaiting Kickoff', label: 'Awaiting Kickoff', color: 'rgb(147, 111, 218)' },
  { value: 'Awaiting Dependencies', label: 'Awaiting Dependencies', color: 'rgb(59, 152, 245)' },
  { value: 'Dependencies Received', label: 'Dependencies Received', color: 'rgb(245, 124, 79)' },
  { value: 'Handoff Call', label: 'Handoff Call', color: 'rgba(106, 65, 192, 1)' },
  { value: 'Launched', label: 'Launched', color: 'rgb(52, 211, 145)' },
]

export const NCO_TASK_STATUS_OPTIONS: SelectOption[] = [
  { value: 'Not Started', label: 'Not Started', color: 'rgb(168, 202, 255)' },
  { value: 'In Progress', label: 'In Progress', color: 'rgb(253, 188, 100)' },
  { value: 'Done', label: 'Done', color: 'rgba(80, 200, 120, 1)' },
]

// ============================================================================
// CREATIVE OPTIONS
// ============================================================================

export const CREATIVE_STATUS_OPTIONS: SelectOption[] = [
  { value: 'Staging', label: 'Staging', color: 'rgb(238, 111, 141)' },
  { value: 'In Progress', label: 'In Progress', color: 'rgb(253, 188, 100)' },
  { value: 'Needs AM Review', label: 'Needs AM Review', color: 'rgb(255, 131, 88)' },
  { value: 'Needs Revision', label: 'Needs Revision', color: 'rgb(213, 197, 103)' },
  { value: 'Sent for Approval', label: 'Sent for Approval', color: 'rgb(147, 111, 218)' },
  { value: 'Ready to Launch', label: 'Ready to Launch', color: 'rgb(121, 175, 253)' },
  { value: 'Done', label: 'Done', color: 'rgb(53, 153, 112)' },
  { value: 'Blocked', label: 'Blocked', color: 'rgb(133, 214, 255)' },
  { value: 'On Hold', label: 'On Hold', color: 'rgb(238, 40, 99)' },
  { value: 'Needs Copy', label: 'Needs Copy', color: 'rgb(113, 214, 209)' },
  { value: 'Needs CD Review', label: 'Needs CD Review', color: 'rgb(51, 211, 145)' },
  { value: 'CD Approved', label: 'CD Approved', color: 'rgb(255, 68, 161)' },
  { value: 'N/A', label: 'N/A', color: 'rgb(121, 126, 147)' },
  { value: 'Need Creative Attention', label: 'Need Creative Attention', color: 'rgb(51, 158, 205)' },
  { value: 'New Project Review', label: 'New Project Review', color: 'rgb(78, 115, 167)' },
  { value: 'Needs PM Attention', label: 'Needs PM Attention', color: 'rgb(255, 213, 51)' },
  { value: 'With Freelancer', label: 'With Freelancer', color: 'rgb(205, 146, 130)' },
]

export const CREATIVE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Social Ad', label: 'Social Ad', color: 'rgb(51, 211, 145)' },
  { value: 'Display Ads', label: 'Display Ads', color: 'rgb(253, 188, 100)' },
  { value: 'Social & Display Ads', label: 'Social & Display Ads', color: 'rgb(133, 214, 255)' },
  { value: 'Logo Design', label: 'Logo Design', color: 'rgb(51, 158, 205)' },
  { value: 'Brand Design', label: 'Brand Design', color: 'rgb(181, 125, 227)' },
  { value: 'Video Design', label: 'Video Design', color: 'rgb(53, 153, 112)' },
  { value: 'Website Build', label: 'Website Build', color: 'rgb(121, 175, 253)' },
  { value: 'Troubleshooting', label: 'Troubleshooting', color: 'rgb(213, 197, 103)' },
  { value: 'Marketing Collateral', label: 'Marketing Collateral', color: 'rgb(92, 92, 92)' },
  { value: 'Photography', label: 'Photography', color: 'rgb(255, 213, 51)' },
  { value: 'Website Edits', label: 'Website Edits', color: 'rgb(201, 92, 118)' },
  { value: 'Organic Social', label: 'Organic Social', color: 'rgb(255, 68, 161)' },
  { value: 'Video Ads', label: 'Video Ads', color: 'rgb(255, 123, 208)' },
]

export const CREATIVE_MASTER_STATUS_OPTIONS: SelectOption[] = [
  { value: 'In Progress', label: 'In Progress', color: 'rgb(253, 188, 100)' },
  { value: 'Needs DM Attention', label: 'Needs DM Attention', color: 'rgb(255, 131, 88)' },
  { value: 'Needs AM Attention', label: 'Needs AM Attention', color: 'rgb(51, 158, 205)' },
  { value: 'Need AM Attention', label: 'Need AM Attention', color: 'rgb(255, 123, 208)' },
  { value: 'Needs Review', label: 'Needs Review', color: 'rgb(255, 68, 161)' },
  { value: 'Need Client Feedback', label: 'Need Client Feedback', color: 'rgb(133, 214, 255)' },
  { value: 'Needs Zach Review', label: 'Needs Zach Review', color: 'rgb(33, 201, 223)' },
  { value: 'Done', label: 'Done', color: 'rgb(51, 211, 145)' },
  { value: 'Needs SCS Attention', label: 'Needs SCS Attention', color: 'rgb(134, 180, 202)' },
  { value: 'Postdated (AM)', label: 'Postdated (AM)', color: 'rgb(181, 125, 227)' },
  { value: 'Postdated (DMS)', label: 'Postdated (DMS)', color: 'rgb(147, 111, 218)' },
  { value: 'Launch Campaigns', label: 'Launch Campaigns', color: 'rgb(78, 115, 167)' },
  { value: 'On Hold', label: 'On Hold', color: 'rgb(201, 92, 118)' },
  { value: 'Needs Setup', label: 'Needs Setup', color: 'rgb(119, 122, 229)' },
  { value: 'Ready to Launch', label: 'Ready to Launch', color: 'rgb(53, 153, 112)' },
  { value: 'Waiting on Creative', label: 'Waiting on Creative', color: 'rgb(92, 92, 92)' },
  { value: 'Awaiting Integrations', label: 'Awaiting Integrations', color: 'rgb(121, 175, 253)' },
]

export const WEBSITE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Large Site', label: 'Large Site', color: 'rgb(253, 188, 100)' },
  { value: 'New Site', label: 'New Site', color: 'rgb(51, 211, 145)' },
  { value: 'Website Rebuild', label: 'Website Rebuild', color: 'rgb(51, 158, 205)' },
]

// ============================================================================
// SEO OPTIONS
// ============================================================================

export const SEO_STATUS_OPTIONS: SelectOption[] = [
  { value: 'Staging', label: 'Staging', color: 'rgb(238, 111, 141)' },
  { value: 'On Hold', label: 'On Hold', color: 'rgb(238, 40, 99)' },
  { value: 'Onboarding', label: 'Onboarding', color: 'rgb(110, 193, 232)' },
  { value: 'Stuck', label: 'Stuck', color: 'rgb(232, 105, 125)' },
  { value: 'No Onboarding Sheet', label: 'No Onboarding Sheet', color: 'rgb(255, 213, 51)' },
  { value: 'Paused', label: 'Paused', color: 'rgb(255, 145, 145)' },
  { value: 'Onboarding Completed', label: 'Onboarding Completed', color: 'rgb(52, 211, 145)' },
]

export const SEO_GMB_OPTIONS: SelectOption[] = [
  { value: 'In-Verification', label: 'In-Verification', color: 'rgb(253, 188, 100)' },
  { value: 'Stuck - Video', label: 'Stuck - Video', color: 'rgb(255, 131, 88)' },
  { value: 'Full Access', label: 'Full Access', color: 'rgb(51, 211, 145)' },
  { value: 'Correct - No Access', label: 'Correct - No Access', color: 'rgb(51, 158, 205)' },
  { value: 'Waiting on AM', label: 'Waiting on AM', color: 'rgb(121, 175, 253)' },
  { value: 'Not Started', label: 'Not Started', color: 'rgb(255, 145, 145)' },
  { value: 'Duplicate Listing', label: 'Duplicate Listing', color: 'rgb(181, 125, 227)' },
  { value: 'Integrations Access', label: 'Integrations Access', color: 'rgb(176, 220, 81)' },
  { value: 'Incorrect - No Access', label: 'Incorrect - No Access', color: 'rgb(213, 197, 103)' },
  { value: 'Missing Listing', label: 'Missing Listing', color: 'rgb(23, 90, 99)' },
  { value: 'Access Lost', label: 'Access Lost', color: 'rgb(255, 68, 161)' },
]

// ============================================================================
// TASK OPTIONS
// ============================================================================

export const TASK_MASTER_STATUS_OPTIONS: SelectOption[] = [
  { value: 'Not Started', label: 'Not Started', color: 'rgb(119, 129, 129)' },
  { value: 'In Progress', label: 'In Progress', color: 'rgb(253, 188, 100)' },
  { value: 'On Hold', label: 'On Hold', color: 'rgb(201, 92, 118)' },
  { value: 'Needs Review', label: 'Needs Review', color: 'rgb(33, 201, 223)' },
  { value: 'Need More Info', label: 'Need More Info', color: 'rgb(183, 91, 249)' },
  { value: 'Done', label: 'Done', color: 'rgb(51, 211, 145)' },
]

export const TASK_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Updates', label: 'Updates', color: 'rgb(147, 111, 218)' },
  { value: 'Review / Audit', label: 'Review / Audit', color: 'rgb(255, 131, 88)' },
  { value: 'Budget', label: 'Budget', color: 'rgb(51, 158, 205)' },
  { value: 'Create / Setup', label: 'Create / Setup', color: 'rgb(25, 117, 255)' },
  { value: 'New Client Onboard', label: 'New Client Onboard', color: 'rgb(52, 211, 145)' },
  { value: 'Campaign Checklists', label: 'Campaign Checklists', color: 'rgb(133, 214, 255)' },
  { value: 'Ad Approvals', label: 'Ad Approvals', color: 'rgb(255, 68, 161)' },
  { value: 'Pause', label: 'Pause', color: 'rgb(255, 145, 145)' },
  { value: 'Enable', label: 'Enable', color: 'rgba(3, 201, 169, 1)' },
]

export const TASK_PRIORITY_OPTIONS: SelectOption[] = [
  { value: 'Low', label: 'Low', color: 'rgb(152, 214, 129)' },
  { value: 'Medium', label: 'Medium', color: 'rgb(253, 188, 100)' },
  { value: 'High', label: 'High', color: 'rgb(255, 131, 88)' },
  { value: 'Urgent', label: 'Urgent', color: 'rgb(235, 77, 77)' },
]

// ============================================================================
// LEAD OPTIONS
// ============================================================================

export const LEAD_STATUS_OPTIONS: SelectOption[] = [
  { value: 'New', label: 'New', color: 'rgb(92, 179, 253)' },
  { value: 'Contacted', label: 'Contacted', color: 'rgb(187, 124, 239)' },
  { value: 'Qualified', label: 'Qualified', color: 'rgb(152, 214, 129)' },
  { value: 'Proposal', label: 'Proposal', color: 'rgb(242, 140, 40)' },
  { value: 'Negotiation', label: 'Negotiation', color: 'rgb(93, 79, 251)' },
  { value: 'Won', label: 'Won', color: 'rgb(80, 200, 120)' },
  { value: 'Lost', label: 'Lost', color: 'rgb(235, 77, 77)' },
]

export const LEAD_QUALITY_OPTIONS: SelectOption[] = [
  { value: 'Hot', label: 'Hot', color: 'rgb(235, 77, 77)' },
  { value: 'Warm', label: 'Warm', color: 'rgb(242, 140, 40)' },
  { value: 'Cold', label: 'Cold', color: 'rgb(92, 179, 253)' },
]

// ============================================================================
// COLOR LOOKUP HELPERS
// ============================================================================

/** Get status color lookup for quick access */
export const createColorLookup = (options: SelectOption[]): Record<string, string> =>
  Object.fromEntries(options.filter(opt => opt.color).map(opt => [opt.value, opt.color!]))

export const ORG_STATUS_COLORS = createColorLookup(ORG_STATUS_OPTIONS)
export const DEAL_STATUS_COLORS = createColorLookup(DEAL_STATUS_OPTIONS)
export const DEAL_STAGE_COLORS = createColorLookup(DEAL_STAGE_OPTIONS)
export const NCO_STATUS_COLORS = createColorLookup(NCO_STATUS_OPTIONS)
export const CREATIVE_STATUS_COLORS = createColorLookup(CREATIVE_STATUS_OPTIONS)
export const TASK_STATUS_COLORS = createColorLookup(TASK_MASTER_STATUS_OPTIONS)

// ============================================================================
// OPTIONS REGISTRY
// Maps optionsKey from entityConfigs to actual options arrays
// ============================================================================

export const OPTIONS_REGISTRY: Record<string, SelectOption[]> = {
  // Organization options
  orgStatusOptions: ORG_STATUS_OPTIONS,
  statusOptions: ORG_STATUS_OPTIONS,
  typeOptions: ORG_TYPE_OPTIONS,
  organizationTypeOptions: ORG_TYPE_OPTIONS,
  verticalOptions: VERTICAL_OPTIONS,
  trackingTypeOptions: TRACKING_TYPE_OPTIONS,
  discoveryOptions: DISCOVERY_OPTIONS,

  // Deal options
  dealPageStatusOptions: DEAL_PAGE_STATUS_OPTIONS,
  contractStatusOptions: CONTRACT_STATUS_OPTIONS,
  dealTypeOptions: DEAL_TYPE_OPTIONS,
  dealStatusOptions: DEAL_STATUS_OPTIONS,
  dealStageOptions: DEAL_STAGE_OPTIONS,

  // NCO options
  ncoStatusOptions: NCO_STATUS_OPTIONS,
  ncoTaskStatusOptions: NCO_TASK_STATUS_OPTIONS,

  // Creative options
  creativeStatusOptions: CREATIVE_STATUS_OPTIONS,
  creativeTypeOptions: CREATIVE_TYPE_OPTIONS,
  creativeMasterStatusOptions: CREATIVE_MASTER_STATUS_OPTIONS,
  websiteTypeOptions: WEBSITE_TYPE_OPTIONS,

  // SEO options
  seoStatusOptions: SEO_STATUS_OPTIONS,
  seoGmbOptions: SEO_GMB_OPTIONS,

  // Task options
  taskMasterStatusOptions: TASK_MASTER_STATUS_OPTIONS,
  taskStatusOptions: TASK_MASTER_STATUS_OPTIONS,
  taskTypeOptions: TASK_TYPE_OPTIONS,
  taskPriorityOptions: TASK_PRIORITY_OPTIONS,

  // Lead options
  leadStatusOptions: LEAD_STATUS_OPTIONS,
  leadQualityOptions: LEAD_QUALITY_OPTIONS,
}

/**
 * Get options by key from the registry
 */
export const getOptions = (optionsKey: string): SelectOption[] => {
  return OPTIONS_REGISTRY[optionsKey] || []
}

/**
 * Get color for a status value
 */
export const getStatusColor = (status: string, optionsKey = 'orgStatusOptions'): string | undefined => {
  const options = OPTIONS_REGISTRY[optionsKey]
  return options?.find(opt => opt.value === status)?.color
}

/**
 * Convert options to filter format (with key property)
 */
export const toFilterOptions = (options: SelectOption[], filterKey: string) =>
  options.map(opt => ({ ...opt, key: filterKey }))
